import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import AdminLayout from '../../components/layout/AdminLayout';
import api from '../../utils/api';

const COLORS = ["#1A6B3C","#2563EB","#D97706","#DC2626","#7C3AED","#0369A1"];

export default function AnalyticsPage() {
  const [stats, setStats] = useState(null);
  const [cropStats, setCropStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      api.get("/admin/stats"),
      api.get("/admin/crops/stats"),
    ]).then(([s, cs]) => {
      if (s.status==="fulfilled") setStats(s.value.data.stats);
      if (cs.status==="fulfilled") setCropStats(cs.value.data.stats);
    }).finally(() => setLoading(false));
  }, []);

  const userChartData = stats ? [
    { name:"Farmers", value:stats.users?.farmers||0, fill:"#1A6B3C" },
    { name:"Buyers",  value:stats.users?.buyers||0,  fill:"#2563EB" },
    { name:"Agents",  value:stats.users?.agents||0,  fill:"#7C3AED" },
  ] : [];

  const cropStatusData = cropStats ? [
    { name:"Growing",       value:cropStats.growing||0 },
    { name:"Ready to Sell", value:cropStats.ready||0 },
    { name:"Sold",          value:cropStats.sold||0 },
    { name:"Closed",        value:cropStats.closed||0 },
  ] : [];

  const categoryData = (cropStats?.byCategory||[]).slice(0,6).map(c => ({
    name: (c._id||"other").charAt(0).toUpperCase()+(c._id||"other").slice(1),
    count: c.count
  }));

  if (loading) return <AdminLayout title="Analytics"><div className="page-loader"><span className="spinner spinner--lg" /></div></AdminLayout>;

  return (
    <AdminLayout title="Analytics & Reports">
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:20 }}>
        <div className="card">
          <div className="card__header"><div className="card__title">👥 Users by Role</div></div>
          <div className="card__body">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={userChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize:12 }} />
                <YAxis tick={{ fontSize:12 }} />
                <Tooltip />
                <Bar dataKey="value" name="Users" radius={[4,4,0,0]}>
                  {userChartData.map((entry,i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card__header"><div className="card__title"> Crops by Status</div></div>
          <div className="card__body">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={cropStatusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({name,value})=>name+": "+value}>
                  {cropStatusData.map((entry,i) => <Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__header"><div className="card__title">📊 Crops by Category</div></div>
        <div className="card__body">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={categoryData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize:12 }} />
              <YAxis tick={{ fontSize:12 }} />
              <Tooltip />
              <Bar dataKey="count" name="Crops" fill="#1A6B3C" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card" style={{ marginTop:20 }}>
        <div className="card__header"><div className="card__title">👨‍🌾 Top Farmer Leads</div></div>
        <div className="card__body--sm">
          <FarmerLeads />
        </div>
      </div>
    </AdminLayout>
  );
}

function FarmerLeads() {
  const [leads, setLeads] = useState([]);
  useEffect(() => {
    api.get("/admin/crops/farmer-leads").then(({ data }) => setLeads(data.leads||[])).catch(() => {});
  }, []);
  if (!leads.length) return <div style={{ color:"#6C757D", fontSize:13, padding:16, textAlign:"center" }}>No data yet</div>;
  return (
    <div className="table-wrap">
      <table>
        <thead><tr><th>#</th><th>Farmer</th><th>Mobile</th><th>Location</th><th>Total</th><th>Growing</th><th>Ready</th><th>Sold</th></tr></thead>
        <tbody>
          {leads.slice(0,10).map((lead,i) => (
            <tr key={lead._id}>
              <td style={{ fontWeight:700, color:"#1A6B3C" }}>#{i+1}</td>
              <td style={{ fontWeight:600 }}>{lead.farmer?.name||"—"}</td>
              <td style={{ color:"#2563EB", fontSize:12 }}>+91 {lead.farmer?.mobile}</td>
              <td style={{ fontSize:12, color:"#6C757D" }}>{(lead.farmer?.locationAddress||"—").split(",")[0]}</td>
              <td style={{ textAlign:"center", fontWeight:700 }}>{lead.totalCrops}</td>
              <td style={{ textAlign:"center", color:"#16A34A" }}>{lead.growing}</td>
              <td style={{ textAlign:"center", color:"#2563EB" }}>{lead.ready}</td>
              <td style={{ textAlign:"center", color:"#D97706" }}>{lead.sold}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
